import { create } from 'zustand';
import axios from 'axios';

const logLevel = 0; // Hier kannst du den Log-Level setzen (0-3)


const expirationMinutes: Record<string, number> = {
    uploading: 5,
    transcribing: 10,
    generating: 3,
    processing: 30,
    waiting: 30,
};


// Interface für Sitzungsdaten
interface TranscriptData {
    Text: string;
    Model: string;
}
interface SessionListData {
    id: number;
    name: string;
    date: string;
    length: number;
    status: string;
    has_documentation: boolean;
    has_transcription: boolean;
    
    tempStatus?: string;
    status_timestamp?: string;
    last_task?: string;
    template_used?: string;
    recording_type?: string;
    audio_deleted?: boolean;
    transcript_deleted?: boolean;
    documentation_locked?: boolean;
    input_source?: string;
    entity_type?: 'session' | 'case';

};

interface SessionData {
    id: number;
    name: string;
    date: string;
    length: string;
    audio_file_name?: string;
    input_source?: string;
    entity_type?: 'session' | 'case';
    gender?: 'male' | 'female' | 'other';
    client_label?: 'client' | 'patient' | 'customer';
    location?: 'in_person' | 'online' | 'clients_home' | 'other';
    session_type?: 'intake' | 'progress_note' | 'biography' | 'supervision';
    participation_type?: 'individual' | 'couple' | 'group' | 'other';
    template_used?: string;
    recording_type?: string;
    transcript_type?: string
    audio_deleted?: boolean;
    transcript_deleted?: boolean;
    documentation_locked?: boolean;

    documentation: { [key: string]: string };  // Dokumentation als JSON-Objekt speichern

    data?: {
        Transcript?: TranscriptData;
        [key: string]: any;
    };
}

// Zustand-Store für Sitzungsdaten
interface SessionStore {
    sessionData: SessionData | null;
    sessions: SessionListData[]; // Liste aller Sessions
    isSessionSelected: boolean; // ✅ Neuer Zustand für Sitzungsauswahl
    selectedSessionId: number | null; // 👈 NEU
    isAudioDialogOpen: boolean;
    setIsAudioDialogOpen: (open: boolean) => void;

    setIsSessionSelected: (selected: boolean) => void; // ✅ Funktion zum Ändern des Werts
    setSelectedSessionId: (id: number | null) => void; // 👈 NEU
    setSessions: (sessions: SessionListData[]) => void;
    fetchSessions: () => Promise<SessionListData[] | null>;
    fetchSessionDataById: (id: number) => Promise<SessionData | null>;
    updateSession: (id: number, updatedFields: Partial<SessionData>) => Promise<boolean>;
    updateSessionTempStatus: (id: number, status: string) => void;
    clearTempStatus: (id: number) => void;
    reset: () => void;
}

// Zustand Store definieren
export const useSessionStore = create<SessionStore>((set, get) => ({
    
    
    sessionData: null,
    isSessionSelected: false, // Standardwert: keine Sitzung ausgewählt
    selectedSessionId: null,
    sessions: [],
    isAudioDialogOpen: false,
    setIsAudioDialogOpen: (open) => set({ isAudioDialogOpen: open }),

    setSelectedSessionId: (id) => set({ selectedSessionId: id }),
    setIsSessionSelected: (selected) => set({ isSessionSelected: selected }),
    setSessions: (sessions) => set({ sessions }),


    fetchSessionDataById: async (id) => {
        
        try {
            const response = await axios.get(`/api/apps/fetch-session/${id}`);
            if (response.status === 200) {
                const sessionData = response.data;
                if (logLevel >= 3) console.log('✅ Sitzung geladen:', sessionData);
                set({ sessionData, isSessionSelected: true }); // ✅ Sitzung als ausgewählt markieren
                return sessionData;
            }
        } catch (error) {
            if (logLevel >= 1) console.error('❌ Fehler beim Laden der Sitzung:', error);
        }
        return null;
    },

    fetchSessions: async () => {
        
        try {
            const response = await axios.get('/api/apps/fetch-sessions/');
            if (response.status === 200) {
                const data = response.data;
                const sorted = data.sort((a: any, b: any) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                const tempStatusMap = JSON.parse(localStorage.getItem('tempStatusMap') || '{}');
                const enriched = sorted.map((s: any) => {
                    const tempStatus = tempStatusMap[s.id];
                    const backendTimestamp = s.status_timestamp ? new Date(s.status_timestamp).getTime() : 0;
                    const tempTimestampKey = `tempStatusTime_${s.id}`;
                    const tempTimestamp = parseInt(localStorage.getItem(tempTimestampKey) || '0');
                    const expireKey = `tempStatusExpire_${s.id}`;
                    const expireTimestamp = parseInt(localStorage.getItem(expireKey) || '0');
                    const isExpired = Date.now() > expireTimestamp;

                    if (logLevel >= 0) {
                        console.log(`🗂️ Sitzung ${s.id}:`);
                        console.log(`   ⏱️ Backend-Status: ${s.status} @ ${new Date(backendTimestamp).toISOString()}`);
                        console.log(`   🕒 Temp-Status: ${tempStatus} @ ${new Date(tempTimestamp).toISOString()}`);
                        console.log(`   ❗ Abgelaufen: ${isExpired}`);
                    }

                    let resolvedStatus = s.status;
                    if (tempStatus && tempTimestamp > backendTimestamp && !isExpired) {
                        resolvedStatus = tempStatus;
                    } else {
                        // Falls Backend-Timestamp aktueller oder gleich alt oder Status abgelaufen: tempStatus entfernen
                        delete tempStatusMap[s.id];
                        localStorage.removeItem(tempTimestampKey);
                        localStorage.removeItem(expireKey);
                    }

                    return {
                        ...s,
                        tempStatus: resolvedStatus,
                    };
                });

                localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusMap));
                set({ sessions: enriched });
                return enriched;
            }
        } catch (error) {
            if (logLevel >= 1) console.error("❌ Fehler beim Laden aller Sitzungen:", error);
        }
        return null;
    },

    updateSessionTempStatus: (id: number, status: string) => {
        const tempStatusStore = JSON.parse(localStorage.getItem('tempStatusMap') || '{}');
        tempStatusStore[id] = status;
        localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusStore));
        const timestampKey = `tempStatusTime_${id}`;
        localStorage.setItem(timestampKey, Date.now().toString());
        const expirationKey = `tempStatusExpire_${id}`;
        const durationMinutes = expirationMinutes[status] || 30;
        const expirationTime = Date.now() + durationMinutes * 60 * 1000;
        localStorage.setItem(expirationKey, expirationTime.toString());

        set((state) => ({
            sessions: state.sessions.map((s) =>
                s.id === id ? { ...s, tempStatus: status } : s
            ),
        }));
    },

    clearTempStatus: (id: number) => {
        const tempStatusStore = JSON.parse(localStorage.getItem('tempStatusMap') || '{}');
        delete tempStatusStore[id];
        localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusStore));
    },

    reset: () => set(() => ({
        sessionData: null,
        sessions: [],
        isSessionSelected: false,
        selectedSessionId: null,
        isAudioDialogOpen: false
    })),

    // ✅ Generische Update-Funktion für Sitzungen
    updateSession: async (id, updatedFields) => {
        
        try {
            const response = await axios.put('/api/apps/update-session/', { id, ...updatedFields });

            if (response.status === 200) {
                if (logLevel >= 3) console.log('✅ Sitzung aktualisiert:', updatedFields);
                set((state) => ({
                    sessionData: state.sessionData
                        ? { ...state.sessionData, ...updatedFields }
                        : null,
                }));
                return true;
            }
        } catch (error) {
            if (logLevel >= 1) console.error('❌ Fehler beim Aktualisieren der Sitzung:', error);
        }
        return false;
    }
}));


// Mapping-Objekte für Anzeigezwecke
export const labelMap: Record<string, string> = {
    // Labels für Doku Anzeige
    male: 'Männlich',
    female: 'Weiblich',
    client: 'Klient',
    patient: 'Patient',
    customer: 'Kunde',
    in_person: 'Persönlich',
    online: 'Online',
    clients_home: 'Hausbesuch',
    intake: 'Erstgespräch',
    progress_note: 'Verlaufsdokumentation',
    supervision: 'Supervision',
    individual: 'Einzel',
    couple: 'Paar',
    group: 'Gruppe',
    dictate: 'Diktat',
    recording: 'Live-Aufnahme',
    upload: 'Upload',
    discribtion: 'Beschreibung',

    // Labels für Statusanzeige
    transcribing: 'Audio wird transkribiert...',
    failed: 'Anfrage fehlgeschlagen',
    completed: 'Abgeschlossen',
    processing: 'Anfrage wird verarbeitet ...',
    waiting: 'Anfrage wird vorbereitet ...',
    unknown: 'Unbekannt',
    generating: 'Wird generiert...',
};